import React, { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Autocomplete, Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';

import { TextSetting } from './BlockSettings';
import { pageBlocks } from './editor';
import { type LensDocument, type LensDocumentData, type PageEntry, type Section, isSection, isTabs } from './types';
import type { BoardEditor } from './useBoardEdit';

function sectionsOf(page: Array<PageEntry>): Array<Section> {
  return page.flatMap((entry) => (isSection(entry) ? [entry] : isTabs(entry) ? entry.tabs.flatMap((tab) => sectionsOf(tab.page)) : []));
}

function SectionRow({ section, editor }: { section: Section; editor: BoardEditor }) {
  const [removing, setRemoving] = useState(false);
  const count = pageBlocks([section]).length;
  return (
    <Stack spacing={1} sx={{ borderLeft: 2, borderColor: 'divider', pl: 1.5, position: 'relative', '&:not(:hover, :focus-within) .remove-section': { opacity: 0 } }}>
      {removing ? null : (
        <Tooltip title="Remove section">
          <IconButton aria-label={`Remove section ${section.section}`} className="remove-section" disabled={editor.pending} onClick={() => setRemoving(true)} size="small" sx={{ position: 'absolute', right: -6, top: -4, zIndex: 1 }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <TextSetting label="Heading" onCommit={(title) => title && title !== section.section && editor.apply({ op: 'set', id: section.id, patch: { section: title } })} value={section.section} />
      <TextSetting label="Lead" onCommit={(lead) => editor.apply({ op: 'set', id: section.id, patch: { lead: lead || null } })} value={section.lead ?? ''} />
      {removing ? (
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Button disabled={editor.pending} onClick={() => editor.apply({ op: 'remove', id: section.id, keep: true })} size="small" variant="contained">
            Remove the heading, keep its {count} blocks
          </Button>
          <Button color="error" disabled={editor.pending} onClick={() => editor.apply({ op: 'remove', id: section.id })} size="small" variant="text">
            Remove it and its blocks
          </Button>
          <Button onClick={() => setRemoving(false)} size="small" variant="text">
            Keep it
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
}

export default function SectionsEditor({ document, data, editor }: { document: LensDocument; data?: LensDocumentData; editor: BoardEditor }) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [contents, setContents] = useState<Array<string>>([]);
  const sections = sectionsOf(document.page);
  const blocks = pageBlocks(document.page);
  const named = (id: string) => {
    const block = blocks.find((entry) => entry.id === id);
    return (typeof block?.title === 'string' && block.title) || data?.blocks[id]?.name || id;
  };
  return (
    <Stack data-lens-sections spacing={1.5}>
      {sections.length ? null : (
        <Typography color="text.secondary" variant="caption">
          No sections.
        </Typography>
      )}
      {sections.map((section) => (
        <SectionRow editor={editor} key={section.id} section={section} />
      ))}
      {adding ? (
        <Stack spacing={1}>
          <TextField label="Heading" onChange={(event) => setTitle(event.target.value)} placeholder="Performance overview" size="small" value={title} />
          <Autocomplete
            getOptionLabel={named}
            multiple
            onChange={(_, next) => setContents(next)}
            options={blocks.map((block) => block.id)}
            renderInput={(params) => <TextField {...params} helperText="The section goes where the first of them is." label="Blocks under it" size="small" />}
            size="small"
            value={contents}
          />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={() => setAdding(false)} size="small" variant="text">
              Cancel
            </Button>
            <Button
              disabled={editor.pending || !title.trim()}
              onClick={() => {
                editor.apply({ op: 'section', title: title.trim(), contents });
                setAdding(false);
                setTitle('');
                setContents([]);
              }}
              size="small"
              variant="contained"
            >
              Add section
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Box>
          <Button disabled={editor.pending} onClick={() => setAdding(true)} size="small" startIcon={<AddIcon />} variant="text">
            Add a section
          </Button>
        </Box>
      )}
    </Stack>
  );
}
